import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  SchoolTimings,
  StaffAttendanceDayList,
  StaffAttendanceReport,
  StaffAttendanceStatus,
  StaffPunch,
} from '@/types/staffAttendance';
import type { InstituteLeaveList } from '@/types/staffLeave';

/**
 * Every call the office's punch-in screen makes.
 *
 * As with the rest of the institute area there is no institute id in any path — the
 * server takes it from the caller's token — and no staff id either: a punch is
 * addressed by its own uuid, so there is nothing here for a client to point at
 * a person and invent an arrival for them.
 */
export const staffAttendanceApi = {
  /** Everybody who signed in on one day. Omitting the date means today. */
  /** Every member of staff's attendance summed from `from` to `to`, both included. */
  report: (from: string, to: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StaffAttendanceReport>>('/institute/staff-attendance/report', {
        params: { from, to },
      }),
    ),

  /** Every member of staff's leave applications whose dates touch the window. */
  leave: (from: string, to: string) =>
    unwrap(
      apiClient.get<ApiSuccess<InstituteLeaveList>>('/institute/staff-leave', {
        params: { from, to },
      }),
    ),

  day: (date?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StaffAttendanceDayList>>('/institute/staff-attendance', {
        params: date ? { date } : undefined,
      }),
    ),

  /**
   * The office's decision on one punch.
   *
   * Only `APPROVED` and `REJECTED` are accepted — deciding again is how a
   * mistake is undone, and it leaves a record of who did it. Reverting to
   * "nobody has looked at this" would erase that, so the server refuses it.
   */
  review: (uuid: string, status: Extract<StaffAttendanceStatus, 'APPROVED' | 'REJECTED'>) =>
    unwrap(apiClient.patch<ApiSuccess<StaffPunch>>(`/institute/staff-attendance/${uuid}`, { status })),

  /**
   * Writes, changes or — with an empty string — clears the office's note on one
   * member of staff's day, whether or not they punched in on it.
   */
  setNote: (staffUuid: string, date: string, note: string) =>
    unwrap(
      apiClient.put<ApiSuccess<{ staffUuid: string; date: string; note: string | null }>>(
        `/institute/staff-attendance/notes/${staffUuid}/${date}`,
        { note },
      ),
    ),

  /** The school's opening and closing time. */
  timings: () => unwrap(apiClient.get<ApiSuccess<SchoolTimings>>('/institute/staff-attendance/timings')),

  /** Changes them. Needs `INSTITUTE_PROFILE_EDIT`; takes effect at once. */
  updateTimings: (timings: SchoolTimings) =>
    unwrap(apiClient.put<ApiSuccess<SchoolTimings>>('/institute/staff-attendance/timings', timings)),
};
